import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../../ui/input";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { CalendarIcon } from "lucide-react";
import {
  format as dfFormat,
  parse as dfParse,
  isValid,
  isAfter,
  startOfDay,
} from "date-fns";

export function DatePicker({
  item,
  value,
  error,
  helperText,
  disabled,
  onFieldChange,
  className,
}) {
  const displayFormat = "MM/dd/yyyy";
  const parsePattern = "MM/dd/yyyy";
  const disableFuture = Boolean(item?.disableFuture);
  const placeholder = item?.placeholder || "mm/dd/yyyy";

  // Optional year range overrides from item
  const startYear = item?.startYear;
  const endYear = item?.endYear;

  const containerRef = useRef(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  useEffect(() => {
    if (selectedDate) setInputValue(dfFormat(selectedDate, displayFormat));
    else setInputValue("");
  }, [selectedDate]);

  // MM/dd/yyyy live format
  const liveFormat = (raw) => {
    const digits = String(raw || "")
      .replace(/\D/g, "")
      .slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  const parseInput = (str) => {
    const normalized = String(str || "").replace(/\s+/g, "");
    if (!normalized || normalized.length < 8) return null;
    
    try {
      const d = dfParse(normalized, parsePattern, new Date());
      // Additional validation to ensure the parsed date is reasonable
      if (isValid(d) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
        return d;
      }
    } catch (error) {
      console.warn("Date parsing error:", error);
    }
    return null;
  };

  const emitIfAcceptable = (date) => {
    if (!date) return;
    const today = startOfDay(new Date());
    if (disableFuture && isAfter(date, today)) return;
    // Format date as YYYY-MM-DD string
    const dateString = dfFormat(date, "yyyy-MM-dd");
    onFieldChange?.(item.pathToValue, dateString, item.type, item);
  };

  const handleChange = (e) => {
    const live = liveFormat(e.target.value);
    setInputValue(live);
    if (live.replace(/\D/g, "").length === 8) {
      const parsed = parseInput(live);
      if (parsed) {
        // Only emit if the date is acceptable (not future when disabled)
        const today = startOfDay(new Date());
        if (!disableFuture || !isAfter(parsed, today)) {
          emitIfAcceptable(parsed);
        }
      }
    } else if (live === "") {
      onFieldChange?.(item.pathToValue, null, item.type, item);
    }
  };

  const handleBlur = () => {
    if (inputValue && inputValue.replace(/\D/g, "").length === 8) {
      const parsed = parseInput(inputValue);
      if (parsed) {
        if (!disableFuture || !isAfter(parsed, startOfDay(new Date()))) {
          setInputValue(dfFormat(parsed, displayFormat));
          emitIfAcceptable(parsed);
          return;
        } else {
          setInputValue(selectedDate ? dfFormat(selectedDate, displayFormat) : "");
          return;
        }
      }
    }
    setInputValue(selectedDate ? dfFormat(selectedDate, displayFormat) : "");
  };

  const handleSelect = (date) => {
    if (!date) return;
    const today = startOfDay(new Date());
    if (disableFuture && isAfter(date, today)) return;
    setInputValue(dfFormat(date, displayFormat));
    emitIfAcceptable(date);
    setIsCalendarOpen(false);
  };

  const handleKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Tab",
      "Enter",
      "Escape",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (allowed.includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  return (
    <div className="space-y-2">
      <div className="relative" ref={containerRef}>
        <Input
          id={item?.pathToValue}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={item?.disabled || disabled}
          placeholder={placeholder}
          className={`${
            error
              ? "border-red-500 hover:border-red-500 focus:border-red-500"
              : "border-gray-300"
          } ${className || ""}`}
          maxLength={10}
          inputMode="numeric"
          aria-describedby={
            helperText ? `${item?.pathToValue}-desc` : undefined
          }
        />

        <Popover modal open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute right-2 top-1/2 h-7 w-7 -translate-y-1/2 rounded-md text-gray-600 hover:text-gray-800 focus:outline-none"
              disabled={item?.disabled || disabled}
              aria-label="Open calendar"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0" side="bottom" align="end">
            <Calendar
              selected={selectedDate}
              onSelect={handleSelect}
              disableFuture={disableFuture}
              startYear={startYear}
              endYear={endYear}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
